import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import api from "@/lib/api-client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface SimilarMatch {
  paper_id: string;
  title: string;
  authors: string[];
  year: number | null;
  normalized_score: number | null;
  match_type: "full" | "partial";
  explanation: string;
}

interface SimilarPapersModalProps {
  conceptId: string;
  paperId: string;
  reason: string;
  reasonText?: string;
  onClose: () => void;
  onDismissed: (paperIds: string[]) => void;
}

export function SimilarPapersModal({
  conceptId,
  paperId,
  reason,
  reasonText,
  onClose,
  onDismissed,
}: SimilarPapersModalProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["find-similar", conceptId, paperId, reason, reasonText],
    queryFn: () =>
      api
        .post(`api/concepts/${conceptId}/papers/${paperId}/find-similar`, {
          json: { reason, reason_text: reasonText },
          timeout: 120_000,
        })
        .json<{ matches: SimilarMatch[] }>(),
    staleTime: Infinity,
    retry: false,
  });

  const matches = data?.matches ?? [];

  // Full matches start checked, partial start unchecked
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const initialized = useRef(false);

  // Initialize selection when data arrives
  useEffect(() => {
    if (matches.length > 0 && !initialized.current) {
      const fullMatchIds = new Set(
        matches.filter((m) => m.match_type === "full").map((m) => m.paper_id),
      );
      setSelected(fullMatchIds);
      initialized.current = true;
    }
  }, [matches]);

  const togglePaper = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === matches.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(matches.map((m) => m.paper_id)));
    }
  };

  const handleDismiss = () => {
    const ids = Array.from(selected);
    if (ids.length > 0) {
      onDismissed(ids);
    }
  };

  const selectedCount = selected.size;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Find Similar Papers</DialogTitle>
        {isLoading ? (
          <div className="flex items-center gap-3 py-8">
            <Loader2 className="size-4 animate-spin text-blue-400" />
            <span className="text-sm text-muted-foreground">
              Searching for papers matching &ldquo;{reasonText || reason}&rdquo;&hellip;
            </span>
          </div>
        ) : matches.length === 0 ? (
          <div className="py-8 text-center">
            <Search className="mx-auto mb-2 size-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No similar papers found</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="text-sm font-semibold">
                Similar Papers Found
              </div>
              <p className="text-xs text-muted-foreground">
                Matching: <span className="text-blue-400">&ldquo;{reasonText || reason}&rdquo;</span>
                {" · "}
                {matches.length} paper{matches.length !== 1 ? "s" : ""}
              </p>
            </DialogHeader>

            <div className="max-h-[400px] overflow-y-auto">
              {matches.map((m) => (
                <label
                  key={m.paper_id}
                  className={`flex cursor-pointer items-start gap-3 border-b border-border/50 px-1 py-2.5 transition-colors hover:bg-muted/30 ${
                    m.match_type === "partial" ? "bg-amber-500/5" : ""
                  }`}
                >
                  <Checkbox
                    checked={selected.has(m.paper_id)}
                    onCheckedChange={() => togglePaper(m.paper_id)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium leading-snug">
                      {m.title}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {m.authors.join(", ")}{m.year ? `, ${m.year}` : ""}
                      {m.normalized_score != null ? ` · Score: ${m.normalized_score}` : ""}
                    </div>
                    <div
                      className={`mt-0.5 text-[10px] italic ${
                        m.match_type === "partial"
                          ? "text-amber-400"
                          : "text-red-400"
                      }`}
                    >
                      {m.match_type === "partial" && "⚠ "}
                      {m.explanation}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={selectedCount === matches.length ? true : selectedCount > 0 ? "indeterminate" : false}
                  onCheckedChange={toggleAll}
                />
                Select all ({selectedCount} of {matches.length})
              </label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={selectedCount === 0}
                  onClick={handleDismiss}
                >
                  Dismiss {selectedCount} Paper{selectedCount !== 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
